import { types, applySnapshot, isAlive } from "mobx-state-tree";
import { BaseEntity, isNewlyCreated, DictionaryItem } from "modules/common/models/entity";
import { Transport } from "modules/common/models/transport";
import { Notificator } from "modules/common/models/notificator";
import { apiUrls } from "modules/common/services/communication/urls";
import { flow } from "modules/common/models/flow";
import { EMPTY_OBJECT_ID, DATE_TIME_FORMAT } from "modules/common/constants";
import Schema from "../components/details/validation";
import moment from "moment";
import { texts } from "modules/common/texts";
import { nameof } from "modules/common/services/typescript";
import { getFieldLabel } from "modules/common/services/form/fields";

export const AgentsCategoriesDictionaryItem = types
    .compose(
        DictionaryItem,
        types.model({
            description: types.string,
        })
    )
    .named("AgentsCategoriesDictionaryItem");

const AgentsCategoriesBase = types.compose(
    Transport,
    Notificator,
    BaseEntity,
    types.model({
        name: types.string,
        description: types.string,
    })
);

export const AgentsCategories = AgentsCategoriesBase.actions((self) => ({
    load: flow(function* (id: string) {
        try {
            const snapshot = isNewlyCreated(id)
                ? initialState()
                : yield self.transport.get<AgentsCategoriesSnapshotType>(apiUrls.agentsCategories.details(id));

            applySnapshot(self, snapshot);
        } catch (er) {
            self.notify.error(er);
        }
    }),

    save: flow(function* (model: AgentsCategoriesSnapshotType) {
        try {
            const snapshot = self.isNewlyCreated
                ? yield self.transport.put<AgentsCategoriesSnapshotType>(apiUrls.agentsCategories.create(), model)
                : yield self.transport.post<AgentsCategoriesSnapshotType>(
                      apiUrls.agentsCategories.update(self.id),
                      model
                  );

            isAlive(self) && applySnapshot(self, snapshot);
            self.notify.success(texts.messages.saved);

            return true;
        } catch (er) {
            self.notify.error(er);

            return false;
        }
    }),

    delete: flow(function* () {
        if (self.isNewlyCreated) {
            return true;
        }

        try {
            yield self.transport.delete<boolean>(apiUrls.agentsCategories.delete(self.id));
            self.notify.success(texts.messages.removed);

            isAlive(self) && applySnapshot(self, initialState());
            return true;
        } catch (er) {
            self.notify.error(er);
            return false;
        }
    }),
})).named("AgentsCategories");

export type AgentsCategoriesSnapshotType = typeof AgentsCategoriesBase.SnapshotType;
export type AgentsCategoriesType = typeof AgentsCategories.Type;
export type AgentsCategoriesDictionaryItemType = typeof AgentsCategoriesDictionaryItem.Type;

export const initialState = (): AgentsCategoriesSnapshotType => ({
    id: EMPTY_OBJECT_ID,
    name: "",
    created: moment().format(DATE_TIME_FORMAT),
    description: "",
});

export const fields = {
    name: nameof((a: AgentsCategoriesType) => a.name) as string,
    description: nameof((a: AgentsCategoriesType) => a.description) as string,
};

export function formatAgentsCategories(type: AgentsCategoriesSnapshotType | null) {
    let result = "";

    if (type) {
        const schema = Schema();

        result += `${getFieldLabel(fields.name, schema, null)}: ${type.name}\n`;
        result += `${getFieldLabel(fields.description, schema, null)}: ${type.description}\n`;
    }

    return result;
}
